const CACHE_KEY = (source, mediaId) => `media_${source}_${mediaId}`;
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

/**
 * Upload a file directly to S3 via presigned PUT URL.
 * Does NOT use Amplify — sends a raw fetch PUT with Content-Type and Content-Length headers.
 */
export async function uploadToS3(presignedUrl, file) {
  let response;
  try {
    response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
  } catch (networkError) {
    // CORS block or network failure — browser never got a response
    throw new Error(`S3 upload network error (likely CORS): ${networkError.message}`);
  }
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}. ${body}`.trim());
  }
}

/**
 * Get a presigned view URL for a media item, with sessionStorage caching.
 * Re-fetches when the cached URL is within 2 minutes of expiry.
 *
 * @param {string} source  - Cache namespace (e.g. 'crm')
 * @param {string} mediaId - The shared_media_id
 * @param {() => Promise<{ presigned_url: string, expires_at: number }>} fetchFn
 *   - Called to fetch a fresh URL; expires_at is a Unix timestamp in seconds
 * @returns {Promise<string>} The presigned URL
 */
export async function getMediaUrl(source, mediaId, fetchFn) {
  const cacheKey = CACHE_KEY(source, mediaId);
  const cached = sessionStorage.getItem(cacheKey);

  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (parsed.expiresAt > Date.now() + REFRESH_BUFFER_MS) {
        return parsed.presignedUrl;
      }
    } catch {
      // corrupted cache — fall through to fetch
    }
  }

  const result = await fetchFn();
  const entry = {
    presignedUrl: result.presigned_url,
    expiresAt: result.expires_at * 1000,
  };
  sessionStorage.setItem(cacheKey, JSON.stringify(entry));
  return result.presigned_url;
}

/**
 * Fetch a URL as a blob and return an object URL for download/display.
 * Caller is responsible for revoking the object URL when done.
 */
export async function fetchFileAsBlob(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Extract the S3 bucket name from a presigned PUT URL.
 *
 * The presigned-upload response only returns { id, object_key, presigned_url };
 * it never returns the bucket. But the file lands in book-api's OWN bucket
 * (not any FE env bucket), and the only place that bucket name appears on the
 * FE is inside the presigned URL. So a follow-up createSharedMediaFromS3 must
 * read the bucket back out of the URL — passing a hardcoded env bucket makes
 * book-api HeadObject the wrong bucket and fail with "source file not found".
 *
 * Handles both AWS URL styles (book-api's default is virtual-hosted):
 *   virtual-hosted: https://<bucket>.s3.<region>.amazonaws.com/<key>?...
 *   path-style:     https://s3.<region>.amazonaws.com/<bucket>/<key>?...
 *
 * @param {string} presignedUrl
 * @returns {string|null} bucket name, or null if it can't be parsed
 */
export function bucketFromPresignedUrl(presignedUrl) {
  if (!presignedUrl) return null;
  try {
    const { hostname, pathname } = new URL(presignedUrl);
    // Path-style: host is s3.<region>.amazonaws.com, bucket is first path seg.
    if (/^s3[.-]/i.test(hostname)) {
      return pathname.replace(/^\/+/, '').split('/')[0] || null;
    }
    // Virtual-hosted: bucket is the label before ".s3." / ".s3-".
    const match = hostname.match(/^(.+?)\.s3[.-]/i);
    return match ? match[1] : null;
  } catch (e) {
    return null;
  }
}

// IC media fields may be stored as plain objects or DynamoDB-shaped values.
const extractMediaKey = (field) => field?.M?.key?.S || field?.key || null;
const extractMediaBucket = (field) => field?.M?.bucket?.S || field?.bucket || null;
const extractMediaName = (field) => field?.M?.name?.S || field?.name || null;
const stripPrivatePrefix = (raw) => (raw ? raw.replace(/^\/?private\//, '') : raw);

const fileExtension = (name, key) => {
  const source = name || key || '';
  const ext = source.includes('.') ? source.split('.').pop().toLowerCase() : '';
  return ext || 'jpg';
};

/**
 * Push a purchaser's CCCD identity images (front/back) to the opportunity's
 * shared media so they surface in the Documents tab. Fire-and-forget: never
 * throws and never blocks the caller. The backend copies the object from S3
 * and de-duplicates by file name, so repeat calls are safe.
 *
 * @param {(opts: { variables: object }) => Promise<any>} pushFromS3
 *   The bound createSharedMediaFromS3 mutate function.
 * @param {string} opportunityId
 * @param {Array<{ customerId: string, contact: object }>} purchasers
 */
export async function pushIdentityDocuments(pushFromS3, opportunityId, purchasers, uploader = {}) {
  if (!pushFromS3 || !opportunityId || !Array.isArray(purchasers)) return;

  const tasks = [];
  purchasers.forEach(({ customerId, contact }) => {
    if (!customerId || !contact) return;
    [['ic_front', 'front'], ['ic_back', 'back']].forEach(([fieldName, side]) => {
      const field = contact[fieldName];
      const key = extractMediaKey(field);
      if (!key) return;
      const bucket = extractMediaBucket(field) || process.env.REACT_APP_SRB_S3;
      if (!bucket) return;
      const ext = fileExtension(extractMediaName(field), key);
      tasks.push(
        pushFromS3({
          variables: {
            parent_type: 'opportunity',
            parent_id: opportunityId,
            source_bucket: bucket,
            source_key: stripPrivatePrefix(key),
            file_name: `identity_${side}_${customerId}.${ext}`,
            file_type: 'identity',
            ...uploader,
          },
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('pushIdentityDocuments failed', fieldName, customerId, err?.message);
        }),
      );
    });
  });
  await Promise.allSettled(tasks);
}
